import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { CategoryVideoService } from '../instances/category-video';
import { CategoryVideoConverter } from '@/converters';

export const useCategoryVideo = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_CATEGORY_VIDEO', params],
    async () => {
      const [err, data] = await CategoryVideoService.getCategoryVideos(params);
      if (err) {
        throw err;
      }
      return {
        data: CategoryVideoConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useCategoryVideoById = (id: string) => {
  const query = useQuery(
    ['GET_CATEGORY_VIDEO_BY_ID', id],
    async () => {
      const [err, data] = await CategoryVideoService.getCategoryVideoById(id);
      if (err) {
        throw err;
      }
      return CategoryVideoConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostCategoryVideoMutation = () => {
  return useMutation({
    mutationKey: ['POST_CATEGORY_VIDEO'],
    mutationFn: async (payload: any) => {
      const [err, data] = await CategoryVideoService.postCategoryVideo(payload);
      if (err) {
        throw err;
      }
      return CategoryVideoConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutCategoryVideoMutation = () => {
  return useMutation({
    mutationKey: ['PUT_CATEGORY_VIDEO'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await CategoryVideoService.putCategoryVideo(id, payload);
      if (err) {
        throw err;
      }
      return CategoryVideoConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteCategoryVideoMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_CATEGORY_VIDEO'],
    mutationFn: async (id: string) => {
      const [err] = await CategoryVideoService.deleteCategoryVideo(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const useGetCategoryVideoById = () => {
  return useMutation({
    mutationKey: ['GET_CATEGORY_VIDEO_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await CategoryVideoService.getCategoryVideoById(uuid);
      if (err) {
        throw err;
      }
      return CategoryVideoConverter.toDomainFromByIdResponse(data);
    },
  });
};
